/**
 * Injuries, with U3's healing horizon.
 *
 * **This section exists for one sentence**: *"Not healing: you are dehydrated."* The engine
 * has always blocked natural healing above the thirst threshold, and no player could
 * observe it — the arm simply failed to get better, turn after turn, for a reason the game
 * knew and never said. U3 puts `healing` and `healing_note` on the wire; this renders them,
 * and that is the whole feature. It is also the one place the panel connects two of its own
 * sections to each other: the thirst bar three centimetres above is *why* this line says what
 * it says.
 *
 * The note is a joined, localized string from the API (`injuries.healing.*`), so nothing here
 * counts chapters or knows a threshold. The state gets the colour; the server gets the words.
 */
import type { Injury, InjuryHealing } from '@/api/generated';
import { injuryAction } from '@/features/character/actions';
import { injuryClause } from '@/features/character/condition';
import { useT } from '@/i18n/context';
import { Nothing } from '@/ui/Section';

const HEALING_COLOR: Record<InjuryHealing, string> = {
  improving: 'var(--color-ramp-ok)',
  // Stalled is not danger — it is "nothing is happening", which is exactly how it should read.
  stalled: 'var(--color-fg-faint)',
  blocked: 'var(--color-ramp-danger)',
};

interface InjuriesProps {
  injuries: readonly Injury[];
  /**
   * The other half of items-as-affordances. A wound is the clearest case in the section for it: *"not healing:
   * you are dehydrated"* tells the player something is wrong and the line under it is the
   * first move toward fixing it, with no memory of the DM's phrasing required.
   */
  onSuggest: (action: string) => void;
}

export function Injuries({ injuries, onSuggest }: InjuriesProps) {
  const t = useT();

  if (injuries.length === 0) return <Nothing>{t('ui.panel.nothing_broken')}</Nothing>;

  return (
    <ul className="flex flex-col gap-4">
      {injuries.map((injury) => (
        <li key={injury.id}>
          <p className="text-fg font-mono" style={{ fontSize: 'var(--text-meta)' }}>
            {injuryClause(t, injury)}
          </p>
          <p className="text-fg-muted mt-1" style={{ fontSize: 'var(--text-meta)' }}>
            {injury.description}
          </p>
          {injury.healing_note !== null ? (
            <p
              className="mt-1 font-mono"
              style={{ fontSize: 'var(--text-meta)', color: HEALING_COLOR[injury.healing] }}
            >
              {injury.healing_note}
            </p>
          ) : null}
          <p className="text-fg-faint mt-1 font-mono" style={{ fontSize: 'var(--text-meta)' }}>
            {t('ui.panel.since_chapter', { chapter: injury.chapter_received })}
          </p>
          <button
            type="button"
            onClick={() => {
              onSuggest(injuryAction(t, injury));
            }}
            className="text-fg-muted hover:text-accent mt-1.5 flex gap-2 text-left font-mono transition-colors"
            style={{ fontSize: 'var(--text-meta)' }}
          >
            <span aria-hidden="true" className="text-fg-faint select-none">
              ›
            </span>
            <span>{injuryAction(t, injury)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}
